import React from 'react';
import { useTranslation } from 'react-i18next';
import Omega3PlanCard from './Omega3PlanCard';
import Omega3TrustRow from './Omega3TrustRow';
import Omega3ProductAccordions from './Omega3ProductAccordions';
import { getBrowserLocale } from '../../../utils/language';

const PLAN_DAYS = {
  monthly_subscription: 30,
  quarterly_subscription: 90,
  one_month_one_off: 30,
  three_month_one_off: 90,
};


const Omega3PlanSelector = ({
  plans,
  selectedPlanKey,
  onSelect,
  onCheckout,
  checkoutDisabled,
  compact = false,
  hideToggle = false,
  ctaLabelKey = null,
  titleKey = 'Omega3_Purchase_Choose',
  belowPrimaryCta = null,
}) => {
  const { t, i18n } = useTranslation();
  const [isSubscription, setIsSubscription] = React.useState(true);

  const priceFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(getBrowserLocale(i18n.language), {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const renderPrice = (amount) => priceFormatter.format(amount);
  const selectedPlan = plans.find((p) => p.id === selectedPlanKey) || null;
  const selectedPlanRecurring = selectedPlan?.recurring;
  const selectedPlanId = selectedPlan?.id;

  /** The one-off monthly plan is the baseline for all discount calculations */
  const oneMonthOneOffPlan = plans.find((p) => p.duration === '30 days' && !p.recurring)
    ?? plans.find((p) => !p.recurring && Number(p.quantity) === 1);

  React.useEffect(() => {
    if (!selectedPlanId) return;
    setIsSubscription(Boolean(selectedPlanRecurring));
  }, [selectedPlanId, selectedPlanRecurring]);

  const visiblePlans = React.useMemo(
    () => plans.filter((p) => Boolean(p.recurring) === isSubscription),
    [plans, isSubscription],
  );

  const handleToggleMode = () => {
    const nextMode = !isSubscription;
    setIsSubscription(nextMode);
    const match =
      plans.find(
        (p) =>
          Boolean(p.recurring) === nextMode &&
          selectedPlan &&
          Number(p.quantity) === Number(selectedPlan.quantity),
      ) || plans.find((p) => Boolean(p.recurring) === nextMode);
    if (match && match.id !== selectedPlanKey) onSelect(match.id);
  };

  const getPerDay = (plan) => {
    const days = PLAN_DAYS[plan.id] || 30;
    return (plan.amount / days).toLocaleString(getBrowserLocale(i18n.language), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // CTA prices follow the selected plan totals (not monthly unit price).
  const ctaBasePrice = selectedPlan
    ? (selectedPlan.compareBaseAmount ?? (oneMonthOneOffPlan ? oneMonthOneOffPlan.amount * Number(selectedPlan.quantity || 1) : null))
    : null;

  return (
    <div className={compact ? 't-mt-5' : ''}>

      {/* ── Title ────────────────────────────────────── */}
      {!compact && (
        <h3
          className="!t-mb-4 t-font-normal t-text-[24px] t-text-navy"
          style={{ fontFamily: 'DM Serif Display, serif' }}
        >
          {t(titleKey)}
        </h3>
      )}

      {/* ── Plan cards ───────────────────────────────── */}
      <div className="t-flex t-flex-col t-gap-2.5">
        {visiblePlans.map((plan) => {
          const isSelected = plan.id === selectedPlanKey;
          // Compare against buying N individual monthly one-off orders
          const planBase = plan.compareBaseAmount ?? (oneMonthOneOffPlan
            ? oneMonthOneOffPlan.amount * Number(plan.quantity || 1)
            : null);
          const hasDiscount = planBase !== null && planBase > plan.amount;
          const savePercent = hasDiscount
            ? Math.round(((planBase - plan.amount) / planBase) * 100)
            : 0;

          return (
            <Omega3PlanCard
              key={plan.id}
              plan={plan}
              isSelected={isSelected}
              hasDiscount={hasDiscount}
              savePercent={savePercent}
              planBase={planBase}
              renderPrice={renderPrice}
              onSelect={onSelect}
              getPerDay={getPerDay}
              t={t}
            />
          );
        })}
      </div>

      {/* ── Subscription toggle ───────────────────────── */}
      {!hideToggle && (
        <div className="t-mt-4 t-flex t-items-center t-justify-between t-gap-4">
          <div>
            <p className="!t-m-0 t-text-[14px] t-font-bold t-text-navy">
              {t('Omega3_Purchase_SubscribeSave')}
            </p>
            <p className="!t-m-0 t-text-[13px] t-text-g500">
              {t('Omega3_Purchase_SubscribeHint')}
            </p>
          </div>
          <button
            type="button"
            aria-pressed={isSubscription}
            onClick={handleToggleMode}
            className={`t-relative t-inline-flex t-h-6 t-w-11 t-flex-none t-cursor-pointer t-items-center t-rounded-full t-border-0 t-transition-colors ${
              isSubscription ? 't-bg-quiz-teal' : 't-bg-g300'
            }`}
          >
            <span
              className={`t-absolute t-h-4 t-w-4 t-rounded-full t-bg-white t-shadow t-transition-all t-duration-200 ${
                isSubscription ? 't-translate-x-6' : 't-translate-x-1'
              }`}
            />
          </button>
        </div>
      )}

      {/* ── CTA button ───────────────────────────────── */}
      <button
        type="button"
        className="t-mt-4 t-flex t-w-full t-items-center t-justify-center t-gap-1.5 t-rounded-full t-border-0 t-bg-butter t-cursor-pointer t-px-6 t-py-4 t-text-[15px] !t-font-bold t-uppercase t-tracking-[0.03em] t-text-navy t-transition-all hover:t-brightness-95 disabled:t-cursor-not-allowed disabled:t-opacity-55"
        style={{ fontFamily: 'Roboto' }}
        onClick={onCheckout}
        disabled={checkoutDisabled}
      >
        <span>{t(ctaLabelKey || selectedPlan?.ctaLabelKey || 'Omega3_Cta_BuyNow')}</span>
        {isSubscription && selectedPlan && (
          <>
            <span className="t-font-normal t-normal-case t-tracking-normal t-opacity-60">·</span>
            {ctaBasePrice !== null && ctaBasePrice > selectedPlan.amount && (
              <span className="t-line-through t-font-normal t-normal-case t-tracking-normal t-opacity-60">
                {renderPrice(ctaBasePrice)}
              </span>
            )}
            <span className="t-font-normal t-normal-case t-tracking-normal">
              {renderPrice(selectedPlan.amount)}
            </span>
          </>
        )}
        <span className="t-text-xl t-font-normal">→</span>
      </button>

      {belowPrimaryCta}

      {/* ── Availability row ─────────────────────────── */}
      <div className="t-mt-3 t-flex t-items-center t-justify-center t-gap-2">
        <span className="t-h-2 t-w-2 t-flex-none t-rounded-full t-bg-green-500 t-shadow-[0_0_0_3px_rgba(34,197,94,0.2)]" />
        <p className="!t-m-0 t-text-[13px] t-text-g500">
          <strong className="t-font-semibold t-text-quiz-teal">{t('Omega3_Avail_Available')}</strong>
          {' — '}
          {t('Omega3_Avail_Text')}
        </p>
      </div>

      {/* ── Trust row ────────────────────────────────── */}
      <Omega3TrustRow t={t} />

      {/* ── Product info accordions (hidden in compact) ─ */}
      {!compact && <Omega3ProductAccordions t={t} />}
    </div>
  );
};

export default Omega3PlanSelector;
